<template>
  <div class="workstream-detail-container">
    <!-- Top Nav -->
    <div class="page-header">
      <button @click="$router.push({ name: 'workstreams' })" class="btn-back">←</button>
      <h1 class="page-title">{{ ws?.name || 'Loading...' }}</h1>

      <button class="btn-chat-toggle" @click="chatDrawerOpen = true">
        Chat
      </button>
    </div>

    <div v-if="chatDrawerOpen" class="drawer-overlay" @click="chatDrawerOpen = false"></div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <p>Loading workstream details...</p>
    </div>

    <!-- Workstream Content -->
    <div v-else-if="ws" class="workstream-content">
      <!-- Main Workspace: Kanban + Sidebar -->
      <div class="main-workspace">
        <!-- Left Section: Header + Kanban Board -->
        <div class="board-section">
          <!-- Workstream Header -->
          <WorkstreamHeader 
            ref="wsHeader"
            :workstream="ws"
            :task-count="tasks.length"
            :agent-count="0"
            :member-count="ws.member_count || 0"
            @update:description="updateDescription"
            @add-task="showCreateTaskForm = true"
            @goal-created="onGoalCreated"
            @goal-deleted="onGoalDeleted"
          />
          <div class="kanban-board">
            <div class="kanban-column" data-status="todo">
              <div class="column-header">
                <span class="column-title">TODO</span>
                <button class="btn-quick-add" @click="quickAddTask('todo')" title="Quick add task">+</button>
              </div>
              <div class="column-body" @drop="onDrop($event, 'todo')" @dragover.prevent>
                <TaskCard
                  v-for="t in tasksByStatus.todo"
                  :key="t.id"
                  :task="t"
                  :is-selected="false"
                  :goal-label="goalLabelFor(t)"
                  :token-total="tokenTotalFor(t)"
                  @click="openTask(t.id)"
                  @dragstart="onDragStart"
                  @delete="deleteTask"
                />
                <div v-if="tasksByStatus.todo.length === 0" class="empty-column">No tasks</div>
              </div>
            </div>

            <div class="kanban-column" data-status="in_progress">
              <div class="column-header">
                <span class="column-title">IN PROGRESS</span>
                <button class="btn-quick-add" @click="quickAddTask('in_progress')" title="Quick add task">+</button>
              </div>
              <div class="column-body" @drop="onDrop($event, 'in_progress')" @dragover.prevent>
                <TaskCard
                  v-for="t in tasksByStatus.in_progress"
                  :key="t.id"
                  :task="t"
                  :is-selected="false"
                  :goal-label="goalLabelFor(t)"
                  :token-total="tokenTotalFor(t)"
                  @click="openTask(t.id)"
                  @dragstart="onDragStart"
                  @delete="deleteTask"
                />
                <div v-if="tasksByStatus.in_progress.length === 0" class="empty-column">No tasks</div>
              </div>
            </div>

            <div class="kanban-column" data-status="done">
              <div class="column-header">
                <span class="column-title">DONE</span>
                <button class="btn-quick-add" @click="quickAddTask('done')" title="Quick add task">+</button>
              </div>
              <div class="column-body" @drop="onDrop($event, 'done')" @dragover.prevent>
                <TaskCard
                  v-for="t in tasksByStatus.done"
                  :key="t.id"
                  :task="t"
                  :is-selected="false"
                  :goal-label="goalLabelFor(t)"
                  :token-total="tokenTotalFor(t)"
                  @click="openTask(t.id)"
                  @dragstart="onDragStart"
                  @delete="deleteTask"
                />
                <div v-if="tasksByStatus.done.length === 0" class="empty-column">No tasks</div>
              </div>
            </div>
          </div>
          
          <!-- Workstream Files Upload -->
          <div class="ws-files-box">
            <div class="ws-files-header">
              <h3>Workstream Files</h3>
            </div>
            <div class="ws-files-body">
              <div class="ws-file-input-row">
                <input 
                  id="ws-file-input"
                  type="file" 
                  @change="onWsFileSelect"
                  :disabled="wsUploading"
                />
                <button 
                  class="btn btn-primary btn-sm"
                  @click="uploadWsFile"
                  :disabled="!wsSelectedFile || wsUploading"
                >
                  {{ wsUploading ? 'Uploading...' : 'Upload' }}
                </button>
              </div>
              <div class="ws-upload-hint">Attach a file to this workstream.</div>
              <div v-if="wsUploadMessage" class="ws-upload-message success">{{ wsUploadMessage }}</div>
              <div v-if="wsUploadError" class="ws-upload-message error">{{ wsUploadError }}</div>

              <!-- Files list -->
              <div class="ws-files-list">
                <div v-if="wsFilesLoading" class="ws-files-empty">Loading files...</div>
                <div v-else-if="wsFilesError" class="ws-files-empty error">{{ wsFilesError }}</div>
                <div v-else-if="wsFiles.length === 0" class="ws-files-empty">No files uploaded yet.</div>
                <div v-else class="ws-files-grid">
                  <div v-for="file in wsFiles" :key="file.id" class="ws-file-card">
                    <div class="ws-file-header">
                      <div class="ws-file-main">
                        <div class="ws-file-name" :title="wsFileDisplayName(file)">{{ wsFileDisplayName(file) }}</div>
                        <div class="ws-file-meta">{{ formatFileSizeWs(file.metadata?.size || file.size) }} • {{ getFileType(file.content_type) }} • {{ formatDate(file.created_at || file.upload_date) }}</div>
                      </div>
                      <div class="ws-file-actions">
                        <button class="btn btn-sm btn-secondary" @click="downloadWsFile(file)" title="Download">⬇️</button>
                        <button class="btn btn-sm btn-danger" @click="deleteWsFile(file)" title="Delete">🗑️</button>
                      </div>
                    </div>

                    <p class="ws-file-desc">{{ file.description || 'No description' }}</p>

                    <div class="ws-file-meta-grid">
                      <div class="ws-file-meta-row">
                        <span class="ws-file-meta-label">Size:</span>
                        <span class="ws-file-meta-value">{{ formatFileSizeWs(file.metadata?.size || file.size) }}</span>
                      </div>
                      <div class="ws-file-meta-row">
                        <span class="ws-file-meta-label">Type:</span>
                        <span class="ws-file-meta-value">{{ getFileType(file.content_type) }}</span>
                      </div>
                      <div class="ws-file-meta-row">
                        <span class="ws-file-meta-label">Category:</span>
                        <span class="ws-file-meta-value ws-file-category" :class="file.category || 'uncategorized'">{{ file.category || 'Uncategorized' }}</span>
                      </div>
                      <div class="ws-file-meta-row">
                        <span class="ws-file-meta-label">Created:</span>
                        <span class="ws-file-meta-value">{{ formatDate(file.created_at) }}</span>
                      </div>
                    </div>

                    <div v-if="file.tags && file.tags.length" class="ws-file-tags">
                      <span v-for="tag in file.tags" :key="tag" class="tag">{{ tag }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Workstream Members Section -->
          <WorkstreamMembers 
            :workstream-id="id"
            :current-user-role="currentUserRole"
            @members-loaded="onMembersLoaded"
          />
        </div>

        <!-- Unified Chat Sidebar (Always Visible) -->
        <div class="sidebar-section">
          <UnifiedChat
            chat-type="workstream"
            :target-id="id"
            :model-id="selectedModel"
            :chat-mode="chatMode"
            :messages="workstreamMessages"
            :is-typing="false"
            :is-loading="workstreamChatLoading"
            :disabled="!selectedModel && chatMode === 'model'"
            :width="350"
            :show-model-selector="true"
            :available-models="models"
            @send-message="sendChat"
            @quick-prompt="handleQuickPrompt"
            @model-change="handleModelChange"
            @mode-change="handleModeChange"
            @settings="handleSettings"
          />
        </div>
      </div>
    </div>

    <!-- Create Task Modal -->
    <div v-if="showCreateTaskForm" class="modal-overlay" @click="showCreateTaskForm = false">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2>Create Task</h2>
          <button @click="showCreateTaskForm = false" class="modal-close">&times;</button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="createTask">
            <div class="form-group">
              <label for="title">Task Title *</label>
              <input 
                id="title"
                v-model="newTask.title" 
                type="text" 
                placeholder="Enter task title (minimum 3 characters)"
                required
                minlength="3"
                maxlength="160"
              />
              <small class="form-hint">{{ newTask.title.length }}/160 characters</small>
            </div>
            <div class="form-group">
              <label for="task_description">Description *</label>
              <textarea 
                id="task_description"
                v-model="newTask.description" 
                rows="4"
                placeholder="Enter task description (required, max 2000 characters)"
                maxlength="2000"
                required
              ></textarea>
              <small class="form-hint">{{ newTask.description.length }}/2000 characters</small>
            </div>
            <div class="form-group">
              <label for="goal_id">Goal *</label>
              <select id="goal_id" v-model="newTask.goal_id" required>
                <option disabled value="">Select goal</option>
                <option v-for="g in goals" :key="g.id" :value="g.id">{{ g.title || g.description || g.name || g.id }}</option>
              </select>
            </div>
            <div class="form-group">
              <label for="allocated_tokens">Allocated Tokens *</label>
              <input
                id="allocated_tokens"
                v-model.number="newTask.allocated_tokens"
                type="number"
                min="1"
                max="100"
                step="1"
                placeholder="Enter tokens (1-100)"
                required
              />
              <small class="form-hint">Value must be between 1 and 100</small>
            </div>
            <!-- Assignees -->
            <div class="form-group">
              <label>Assignee *</label>
              <div class="assignee-combobox" @keydown.stop>
                <div class="assignee-input-wrap" @click="focusCreateAssignee">
                  <span v-for="(chip, cidx) in createSelectedAssigneeChips" :key="(chip.id || chip._id || chip.email || cidx) + '-chip'" class="assignee-chip">
                    <span class="chip-initials">{{ getInitials(displayFor(chip)) }}</span>
                    <button class="chip-clear" @click.stop="createRemoveAssignee(cidx)" title="Remove">×</button>
                  </span>
                  <input
                    ref="createAssigneeInputRef"
                    v-model="createAssigneeQuery"
                    @input="onCreateAssigneeInput"
                    @focus="createAssigneeOpen = true"
                    @keydown.down.prevent="createMoveAssignee(1)"
                    @keydown.up.prevent="createMoveAssignee(-1)"
                    @keydown.enter.prevent="createConfirmHighlighted()"
                    class="assignee-input"
                    :placeholder="createSelectedAssigneeChips.length ? '' : 'Search user by name or email'"
                    autocomplete="off"
                    aria-required="true"
                  />
                </div>
                <div v-if="createAssigneeOpen" class="assignee-dropdown" @mousedown.prevent>
                  <template v-if="createAssigneeResults.length > 0">
                    <div
                      v-for="(u, i) in createAssigneeResults"
                      :key="u.id || u._id || u.email || i"
                      :class="['assignee-option', { highlighted: i === createHighlightedIndex }]"
                      @mouseenter="createHighlightedIndex = i"
                      @click="createSelectAssignee(u)"
                    >
                      <span class="option-initials">{{ getInitials(displayFor(u)) }}</span>
                      <div class="option-texts">
                        <div class="option-name">{{ displayFor(u) }}</div>
                        <div v-if="u.email" class="option-email">{{ u.email }}</div>
                      </div>
                    </div>
                  </template>
                  <div v-else class="assignee-empty">No users found</div>
                </div>
              </div>
            </div>

            <!-- Due Date -->
            <div class="form-group">
              <label>Due Date *</label>
              <input 
                v-model="newTaskDueDateInput"
                type="date" 
                required
              />
            </div>

            <div class="form-actions">
              <button type="button" @click="showCreateTaskForm = false" class="btn btn-secondary">Cancel</button>
              <button type="submit" class="btn btn-primary" :disabled="!isTaskFormValid">Create Task</button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Select Workstream Agent Modal -->
    <div v-if="showAgentSelectModal" class="modal-overlay" @click="showAgentSelectModal = false">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2>Select Agent</h2>
          <button @click="showAgentSelectModal = false" class="modal-close">&times;</button>
        </div>
        <div class="modal-body">
          <div v-if="agentsLoading" class="loading-container">
            <div class="spinner"></div>
            <p>Loading agents...</p>
          </div>
          <div v-else>
            <div v-if="agentsList.length === 0" class="empty-column">No agents available</div>
            <div v-else class="form-group">
              <label for="agent_select">Agent</label>
              <select id="agent_select" v-model="selectedAgentChoice">
                <option 
                  v-for="a in agentsList" 
                  :key="a.id || a._id" 
                  :value="String(a.id || a._id)"
                >
                  {{ a.name || a.id || a._id }}
                </option>
              </select>
            </div>
            <div class="form-actions">
              <button type="button" class="btn btn-secondary" @click="showAgentSelectModal = false">Cancel</button>
              <button type="button" class="btn btn-primary" :disabled="!selectedAgentChoice" @click="confirmAgentSelection">Assign Agent</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.workstream-detail-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
  max-height: 100vh;
  overflow: hidden;
}

.page-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 12px 16px;
  background: white;
  border-bottom: 1px solid #e9ecef;
  flex-shrink: 0;
}

.btn-back {
  padding: 0.25rem 0.5rem;
  background: transparent;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  font-size: 1.1rem;
  line-height: 1;
  transition: background 0.15s;
  color: #6c757d;
}

.btn-back:hover {
  background: rgba(0,0,0,0.05);
}

.page-title {
  font-size: 16px;
  font-weight: 600;
  color: #37352f !important;
  margin: 0;
  flex: 1;
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;
}

.workstream-content {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 3px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.15s;
}

.btn-sm {
  padding: 0.35rem 0.7rem;
  font-size: 0.85rem;
}

.btn-primary {
  background: #37352f;
  color: white;
}

.btn-primary:hover {
  background: #2d2b27;
}

.btn-secondary {
  background: white;
  color: #37352f;
  border: 1px solid rgba(0,0,0,0.1);
}

.btn-secondary:hover {
  background: rgba(0,0,0,0.03);
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: #a0a0a0;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(0,0,0,0.06);
  border-top: 3px solid #37352f;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 0.75rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 15, 15, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 1rem;
}

.modal-content {
  background: white;
  border-radius: 3px;
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid rgba(0,0,0,0.06);
}

.modal-header h2 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #37352f;
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.3rem;
  cursor: pointer;
  color: #6c757d;
  padding: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 3px;
  transition: all 0.15s;
}

.modal-close:hover {
  background: rgba(0,0,0,0.05);
}

.modal-body {
  padding: 1.25rem;
}

.form-group {
  margin-bottom: 1.25rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.4rem;
  font-weight: 500;
  font-size: 0.85rem;
  color: #37352f;
}

.form-group input,
.form-group textarea,
.form-group select {
  width: 100%;
  padding: 0.6rem 0.75rem;
  border: 1px solid rgba(0,0,0,0.1);
  border-radius: 3px;
  font-size: 0.9rem;
  transition: border-color 0.15s;
}

.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
  outline: none;
  border-color: rgba(0,0,0,0.2);
}

.form-hint {
  display: block;
  margin-top: 0.3rem;
  font-size: 0.75rem;
  color: #a0a0a0;
}

.form-actions {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  margin-top: 1.25rem;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Main Workspace */
.main-workspace {
  flex: 1;
  min-height: 0;
  display: flex;
  gap: 0;
  overflow: hidden;
}

/* Kanban Board Section (65%) */
.board-section {
  flex: 1 1 auto;            /* grow to fill remaining space */
  min-height: 0;
  min-width: 0; /* allow shrink in flex row to avoid horizontal clipping */
  display: flex;
  flex-direction: column;
  padding: 16px 16px 16px 16px;
  overflow-y: auto;
  overflow-x: hidden;
}

/* Workstream Files Upload */
.ws-files-box {
  margin: 16px 0;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  background: #ffffff;
}

.ws-files-header {
  padding: 12px 16px;
  border-bottom: 1px solid #e9ecef;
  background: #f8f9fa;
}

.ws-files-header h3 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #37352f;
}

.ws-files-body {
  padding: 12px 16px;
  min-height: 360px; /* Make box big in height */
  display: flex;
  flex-direction: column;
}

.ws-file-input-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.ws-upload-hint {
  margin-top: 6px;
  font-size: 12px;
  color: #6c757d;
}

.ws-upload-message {
  margin-top: 8px;
  font-size: 12px;
}

.ws-upload-message.success { color: #2e7d32; }
.ws-upload-message.error { color: #c62828; }

/* Files list area */
.ws-files-list { 
  margin-top: 12px; 
  flex: 1; 
  min-height: 260px; 
  display: flex; 
  flex-direction: column; 
}
.ws-files-empty { 
  flex: 1; 
  display: flex; 
  align-items: center; 
  justify-content: center; 
  color: #6c757d; 
  font-size: 13px; 
  text-align: center;
}
.ws-files-empty.error { color: #c62828; }
.ws-files-grid { 
  flex: 1; 
  display: grid; 
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); 
  gap: 10px; 
  overflow-y: auto; 
  padding-top: 6px; 
}
.ws-file-card { 
  display: flex; 
  flex-direction: column;
  gap: 8px; 
  border: 1px solid #e9ecef; 
  border-radius: 6px; 
  padding: 10px 12px; 
  background: #ffffff; 
}
.ws-file-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}
.ws-file-name { 
  font-size: 14px; 
  font-weight: 600; 
  color: #2c3e50; 
  margin: 0 0 2px 0; 
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ws-file-meta { 
  font-size: 12px; 
  color: #6c757d; 
}
.ws-file-actions { 
  display: inline-flex; 
  gap: 6px; 
}
.ws-file-desc {
  margin: 0;
  color: #6c757d;
  font-size: 13px;
}
.ws-file-meta-grid {
  display: grid;
  gap: 6px;
}
.ws-file-meta-row {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
}
.ws-file-meta-label { color: #6c757d; font-weight: 500; }
.ws-file-meta-value { color: #495057; }
.ws-file-category { 
  padding: 2px 8px; 
  border-radius: 12px; 
  font-size: 11px; 
  font-weight: 500; 
  text-transform: capitalize; 
  background: #f5f5f5; 
  color: #616161; 
}
.ws-file-category.documentation { background: #e3f2fd; color: #1976d2; }
.ws-file-category.research { background: #f3e5f5; color: #7b1fa2; }
.ws-file-category.policy { background: #fff3e0; color: #f57c00; }
.ws-file-category.tutorial { background: #e8f5e8; color: #388e3c; }
.ws-file-category.reference { background: #fce4ec; color: #c2185b; }
.ws-file-category.other { background: #f5f5f5; color: #616161; }
.ws-file-category.uncategorized { background: #f5f5f5; color: #9e9e9e; }

.ws-file-tags { display: flex; flex-wrap: wrap; gap: 6px; }
.ws-file-tags .tag { background: #e9ecef; color: #495057; padding: 2px 6px; border-radius: 10px; font-size: 11px; }

/* Sidebar Section (35%) - Always Visible */
.sidebar-section {
  flex: 0 0 400px;           /* match goal/task chat fixed width */
  max-width: 420px;
  min-width: 320px;
  min-height: 0;
  min-width: 0; /* allow shrink in flex row to avoid horizontal clipping */
  display: flex;
  flex-direction: column;
  border-left: 1px solid #e9ecef;
  background: #fafbfc;
}

.btn-chat-toggle {
  display: none;
  padding: 6px 10px;
  font-size: 12px;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #111827;
  border-radius: 6px;
  cursor: pointer;
}

.btn-chat-toggle:hover {
  background: rgba(0,0,0,0.03);
}

.drawer-overlay {
  display: none;
}

/* Board Section */
.board-actions {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 0.5rem;
}

.btn-new-task {
  padding: 0.35rem 0.75rem;
  background: transparent;
  border: 1px solid rgba(0,0,0,0.1);
  border-radius: 3px;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 500;
  color: #37352f;
  transition: all 0.15s;
}

.btn-new-task:hover {
  background: rgba(0,0,0,0.03);
  border-color: rgba(0,0,0,0.15);
}

/* Kanban Board */
.kanban-board {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  min-height: 500px;
}

.kanban-column {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  min-height: 500px;
  overflow: hidden;
}

.column-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 2px solid #e9ecef;
  background: white;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02);
}

.column-title {
  font-weight: 700;
  color: #2c3e50;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.btn-quick-add {
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: none;
  background: transparent;
  color: #6c757d;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}

.btn-quick-add:hover {
  background: rgba(0,0,0,0.05);
}

.column-body {
  flex: 1;
  padding: 12px;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.empty-column {
  text-align: center;
  color: #9ca3af;
  padding: 2rem 1rem;
  font-size: 13px;
  font-style: italic;
}


/* Responsive */
@media (max-width: 1024px) {
  .main-workspace {
    flex-direction: column;
  }
  
  .board-section,
  .sidebar-section {
    flex: none;
    width: 100%;
    min-height: 400px;
  }
}

@media (max-width: 768px) {
  .kanban-board {
    grid-template-columns: 1fr;
  }

  .btn-chat-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .drawer-overlay {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(15, 15, 15, 0.45);
    z-index: 1000;
  }

  .sidebar-section {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    z-index: 1001;
    background: white;
    transform: translateX(100%);
    transition: transform 0.2s ease-in-out;
    box-shadow: -8px 0 24px rgba(0,0,0,0.18);
  }

  .sidebar-section.drawer-open {
    transform: translateX(0);
  }
}
/* Assignee combobox styles (match Edit Task) */
.assignee-combobox { position: relative; }
.assignee-input-wrap { display: flex; align-items: center; flex-wrap: nowrap; gap: 6px; border: 1px solid #e9ecef; border-radius: 4px; padding: 4px 6px; min-height: 38px; overflow-x: auto; overflow-y: hidden; white-space: nowrap; }
.assignee-input-wrap::-webkit-scrollbar { display: none; }
.assignee-input { flex: 1 0 auto; min-width: 120px; width: auto !important; border: none !important; outline: none; font-size: 14px; padding: 6px 4px; background: transparent; box-shadow: none; }
.assignee-chip { flex: 0 0 auto; display: inline-flex; align-items: center; gap: 6px; background: #eef2ff; color: #37352f; border: 1px solid #e0e7ff; border-radius: 999px; padding: 2px 6px; }
.chip-initials { font-weight: 700; font-size: 12px; width: 20px; height: 20px; border-radius: 999px; background: #111827; color:#fff; display:inline-flex; align-items:center; justify-content:center; }
.chip-clear { background: none; border: none; color: #6b7280; cursor: pointer; font-size: 14px; line-height: 1; padding: 0 2px; }
.chip-clear:hover { color: #ef4444; }
.assignee-dropdown { position: absolute; z-index: 2001; left: 0; right: 0; margin-top: 4px; background: #fff; border: 1px solid #e5e7eb; border-radius: 6px; box-shadow: 0 6px 18px rgba(0,0,0,0.08); max-height: 250px; overflow-y: auto; }
.assignee-option { display: flex; align-items: center; gap: 10px; padding: 8px 10px; cursor: pointer; }
.assignee-option:hover, .assignee-option.highlighted { background: #f8fafc; }
.option-initials { width: 24px; height: 24px; border-radius: 50%; background: #111827; color: #fff; display: inline-flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700; }
.option-texts { display: flex; flex-direction: column; min-width: 0; }
.option-name { font-size: 14px; font-weight: 600; color: #111827; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.option-email { font-size: 12px; color: #6b7280; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.assignee-empty { padding: 10px; font-size: 13px; color: #6b7280; }
</style>

<style scoped>
/* Task Details Panel */
.task-details-panel {
  background: white;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

/* Agent Chat Panel */
.chat-section {
  flex: 1;
  border-left: 1px solid #e9ecef;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

/* Panel Headers */
.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #e9ecef;
  background: #f8f9fa;
  flex-shrink: 0;
}

.panel-actions {
  display: inline-flex;
  gap: 8px;
  align-items: center;
}

.btn-close-drawer {
  display: none;
}

@media (max-width: 768px) {
  .btn-close-drawer {
    display: inline-flex;
  }
}

.panel-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #37352f;
}

.btn-close-panel {
  background: none;
  border: none;
  font-size: 20px;
  color: #6c757d;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 3px;
  transition: background 0.15s;
}

.btn-close-panel:hover {
  background: rgba(0,0,0,0.05);
}

.btn-enable-agent {
  padding: 4px 12px;
  font-size: 12px;
  background: #667eea;
  color: white;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  font-weight: 500;
  transition: background 0.15s;
}

.btn-enable-agent:hover {
  background: #5568d3;
}

.loading-text {
  font-size: 12px;
  color: #6c757d;
}

/* Chat Container */
.chat-container {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Empty State */
.empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: #9ca3af;
  text-align: center;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 12px;
  opacity: 0.6;
}

.empty-state p {
  margin: 0 0 4px 0;
  font-size: 14px;
  font-weight: 500;
  color: #6c757d;
}

.empty-state small {
  font-size: 12px;
  color: #9ca3af;
}
</style>

<script>
import { onMounted, ref, computed, watch, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { chatApi, goalsApi, workstreamsApi, agentsApi, tasksApi, usersApi, llmApi } from '@/services/api'
import WorkstreamHeader from '@/components/workstream/WorkstreamHeader.vue'
import TaskCard from '@/components/workstream/TaskCard.vue'

import WorkstreamMembers from '@/components/workstream/WorkstreamMembers.vue'
import UnifiedChat from '@/components/UnifiedChat.vue'

export default {
  name: 'WorkstreamDetailView',
components: {
    WorkstreamHeader,
    TaskCard,
    WorkstreamMembers,
    UnifiedChat
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const store = useStore()
const id = computed(() => route.params.id)
    const ws = computed(() => store.state.workstreams.currentItem)
    const loading = ref(false)
    const workstreamAgentId = ref('')
    const bootstrapLoading = ref(false)
    const tasks = ref([])
    const goals = ref([])
    const selectedGoalFilter = ref('')
    const showCreateTaskForm = ref(false)
    const chatDrawerOpen = ref(false)
    const newTask = ref({ title: '', description: '', goal_id: '', allocated_tokens: null })

    // Create Task: Assignee search state
    const createAssigneeInputRef = ref(null)
    const createAssigneeQuery = ref('')
    const createAssigneeOpen = ref(false)
    const createAssigneeResults = ref([])
    const createHighlightedIndex = ref(-1)
    const createSelectedAssignees = ref([])
    let createAssigneeTimer = null

    const getId = (obj) => obj?.id ?? obj?._id ?? obj?.user_id ?? obj?.userId ?? obj?.uuid ?? null
    const displayFor = (u) => (u?.name || u?.full_name || u?.display_name || u?.email || String(getId(u) || ''))
    const createSelectedAssigneeChips = computed(() => createSelectedAssignees.value.filter(u => !!(u && (u.name || u.display_name || u.full_name || u.email))))

    const focusCreateAssignee = () => { createAssigneeOpen.value = true; nextTick(() => createAssigneeInputRef.value?.focus()) }
    const onCreateAssigneeInput = async () => {
      if (createAssigneeTimer) clearTimeout(createAssigneeTimer)
      const q = createAssigneeQuery.value.trim()
      createAssigneeOpen.value = !!q
      createAssigneeTimer = setTimeout(async () => {
        if (!q) { createAssigneeResults.value = []; createHighlightedIndex.value = -1; return }
        try {
          const res = await usersApi.search(q)
          const list = Array.isArray(res) ? res : (res?.users || [])
          createAssigneeResults.value = list
          createHighlightedIndex.value = list.length > 0 ? 0 : -1
        } catch (e) {
          createAssigneeResults.value = []
          createHighlightedIndex.value = -1
        }
      }, 200)
    }
    const createMoveAssignee = (delta) => {
      if (createAssigneeResults.value.length === 0) return
      const n = createAssigneeResults.value.length
      createHighlightedIndex.value = (createHighlightedIndex.value + delta + n) % n
    }
    const createConfirmHighlighted = () => { if (createHighlightedIndex.value >= 0) createSelectAssignee(createAssigneeResults.value[createHighlightedIndex.value]) }
    const createSelectAssignee = (u) => {
      const id = getId(u)
      if (!id) { createAssigneeQuery.value = ''; createAssigneeOpen.value = false; return }
      const exists = createSelectedAssignees.value.some(x => getId(x) === id)
      if (exists) { createAssigneeQuery.value = ''; createAssigneeOpen.value = false; return }
      createSelectedAssignees.value.push(u)
      if (!newTask.value.assigned_to && createSelectedAssignees.value.length > 0) {
        const prim = createSelectedAssignees.value[0]
        newTask.value.assigned_to = getId(prim)
        newTask.value.assignee_name = displayFor(prim)
      }
      newTask.value.assignee_ids = createSelectedAssignees.value.map(x => getId(x))
      newTask.value.assignees_names = createSelectedAssignees.value.map(x => displayFor(x))
      createAssigneeQuery.value = ''
      createAssigneeOpen.value = false
    }
    const createRemoveAssignee = (index) => {
      if (index < 0 || index >= createSelectedAssignees.value.length) return
      const removed = createSelectedAssignees.value[index]
      const removedId = getId(removed)
      createSelectedAssignees.value.splice(index, 1)
      newTask.value.assignee_ids = createSelectedAssignees.value.map(x => getId(x))
      newTask.value.assignees_names = createSelectedAssignees.value.map(x => displayFor(x))
      if (index === 0) {
        if (createSelectedAssignees.value.length > 0) {
          const prim = createSelectedAssignees.value[0]
          newTask.value.assigned_to = getId(prim)
          newTask.value.assignee_name = displayFor(prim)
        } else {
          newTask.value.assigned_to = null
          newTask.value.assignee_name = ''
        }
      }
      nextTick(() => createAssigneeInputRef.value?.focus())
    }

    // Create Task: Due date input mapping
    const newTaskDueDateInput = computed({
      get() {
        const v = newTask.value?.due_date
        if (!v) return ''
        if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) return v
        const d = new Date(v)
        return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10)
      },
      set(val) {
        if (typeof val === 'string') {
          newTask.value = { ...newTask.value, due_date: val }
        }
      }
    })
    const selectedGoalDetails = ref(null)

    // Agent selection modal state
    const showAgentSelectModal = ref(false)
    const agentsList = ref([])
    const agentsLoading = ref(false)
    const selectedAgentChoice = ref('')

      // Chat loading states and message history
      const workstreamChatLoading = ref(false)
      const workstreamChatHistory = ref([])


    const selectedModel = ref('')
    const chatMode = ref('model')
    const models = ref([])

    const loadModels = async () => {
      try {
        const res = await llmApi.getModels()
        const raw = Array.isArray(res) ? res : (res?.models || [])
        // Normalize so dropdown always has a stable id/name (e.g., Groq entries often only expose model_id)
        models.value = raw
          .map(m => ({
            ...m,
            id: m?.id || m?.model_id || m?.name,          // fall back to model_id so v-model works
            name: m?.name || m?.display_name || m?.model_id || 'Model'
          }))
          .filter(m => !!m.id)
        if (models.value.length > 0 && !selectedModel.value) {
          selectedModel.value = models.value[0].id
        }
      } catch (e) {
        console.warn('Failed to load models', e)
      }
    }

    const loadWorkstreamSessions = async () => {
      try {
        const res = await chatApi.getSessions('workstream', id.value)
        const sessions = Array.isArray(res) ? res : []
        const flattened = sessions.flatMap((s) => Array.isArray(s.messages)
          ? s.messages.map((m, idx) => ({
              role: m.role === 'assistant' ? 'agent' : (m.role === 'system' ? 'system' : 'user'),
              content: m.content,
              timestamp: m.timestamp ? new Date(m.timestamp).toISOString() : new Date().toISOString(),
              _key: `${s.id || 'session'}-${idx}`
            }))
          : []
        )
        flattened.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
        // Store messages in Vuex instead of local ref
        store.commit('chat/SET_MESSAGES', {
          target: 'workstream',
          id: id.value,
          messages: flattened
        })
      } catch (e) {
        console.error('Failed to load workstream chat sessions', e)
        store.commit('chat/SET_MESSAGES', {
          target: 'workstream',
          id: id.value,
          messages: []
        })
      }
    }

    const isAdminOrAbove = computed(() => store.getters['auth/isAdminOrAbove'])

    // Resolve Knowledge Base ID associated with this workstream (best-effort)
    const kbId = computed(() => {
      const w = ws.value || {}
      const meta = w.metadata || w.Metadata || {}
      return (
        // Common fields
        w.knowledge_base_id || w.kb_id || w.kbId ||
        meta.knowledge_base_id || meta.kb_id || meta.kbId ||
        // Route overrides (e.g., ?kb=KB123)
        route.query.kb || route.query.kb_id || null
      )
    })

    const filteredTasks = computed(() => {
      if (!selectedGoalFilter.value) return tasks.value
      return tasks.value.filter(t => String(t.goal_id) === String(selectedGoalFilter.value))
    })

    const tasksByStatus = computed(() => ({
      todo: filteredTasks.value.filter(t => (t.status || 'todo') === 'todo'),
      in_progress: filteredTasks.value.filter(t => t.status === 'in_progress'),
      done: filteredTasks.value.filter(t => t.status === 'done')
    }))

    // Call new API when a goal is selected in the filter
    watch(selectedGoalFilter, async (newVal) => {
      if (!newVal) { selectedGoalDetails.value = null; return }
      try {
        selectedGoalDetails.value = await goalsApi.getInWorkstream(id.value, newVal)
      } catch (e) {
        console.error('Failed to load selected goal via workstream endpoint', e)
      }
    })

    const isTaskFormValid = computed(() => {
      const t = newTask.value.allocated_tokens
      const validTokens = Number.isInteger(t) && t >= 1 && t <= 100
      const titleOk = newTask.value.title.length >= 3 && newTask.value.title.length <= 160
      const descOk = !!(newTask.value.description && newTask.value.description.trim().length > 0)
      const goalOk = newTask.value.goal_id !== '' && newTask.value.goal_id !== null && newTask.value.goal_id !== undefined
      const dueOk = !!(newTask.value.due_date && String(newTask.value.due_date).trim().length > 0)
      const assigneeOk = createSelectedAssignees.value.length > 0
      return titleOk && descOk && goalOk && dueOk && assigneeOk && validTokens
    })

const refreshData = async () => {
      loading.value = true
      try {
        // Fetch workstream (redirect if no access)
        try {
          await store.dispatch('workstreams/fetchById', id.value)
        } catch (e) {
          if (e?.response?.status === 403 || e?.response?.status === 404) {
            try { router.push({ name: 'workstreams' }) } catch (_) {}
            return
          }
        }

        // Tasks
        try {
          tasks.value = await store.dispatch('tasks/listByWorkstream', id.value) || []
        } catch (e) {
          tasks.value = []
        }

        // Goals
        try {
          const res = await goalsApi.listByWorkstream(id.value)
          goals.value = Array.isArray(res) ? res : (res?.goals || [])
        } catch (e) {
          goals.value = []
        }

        // Try to read agent id from various shapes returned by backend
        const meta = ws.value?.metadata || ws.value?.Metadata || {}
        if (meta && typeof meta === 'object' && meta.agent_id) {
          workstreamAgentId.value = String(meta.agent_id)
        } else if (ws.value?.agent_id) {
          workstreamAgentId.value = String(ws.value.agent_id)
        } else if (ws.value?.agent?.id) {
          workstreamAgentId.value = String(ws.value.agent.id)
        } else if (ws.value?.agentId) {
          workstreamAgentId.value = String(ws.value.agentId)
        }
      } finally {
        loading.value = false
      }
    }



    // Computed properties to sync with chat store
    const workstreamMessages = computed(() =>
      store.getters['chat/getMessages']('workstream', id.value)
    )



    const selectTask = (t) => {
      router.push({
        name: 'task-detail',
        params: { id: t.id },
        query: { workstream_id: id.value }
      })
    }
    // Alias for template binding used by TaskCard
    const openTask = (taskId) => {
      if (!taskId) return
      selectTask({ id: taskId })
    }

    // Chat handling methods
    const sendChat = async (message, modelId, mode) => {
      const selectedMode = mode || chatMode.value
      const selectedModelId = modelId || selectedModel.value

      if (!message) return

      // Validate model selection for model mode
      if (selectedMode === 'model' && !selectedModelId) {
        alert('Please select a model before sending messages.')
        return
      }

      try {
        // Workstream chat using Vuex store action
        workstreamChatLoading.value = true
        await store.dispatch('chat/sendWorkstream', {
          id: id.value,
          message: message,
          chatMode: selectedMode,
          modelId: selectedModelId,
          context: {
            workstream_name: ws.value?.name || 'Workstream',
            workstream_description: ws.value?.description || 'A collaborative workspace',
            workstream_id: id.value
          }
        })
      } catch (err) {
        console.error('Error sending chat message:', err)
        alert('Failed to send message: ' + (err?.response?.data?.error || err.message))
      } finally {
        workstreamChatLoading.value = false
      }
    }

    const handleQuickPrompt = (payload) => {
      sendChat(payload)
    }

    const handleModelChange = (modelId) => {
      selectedModel.value = modelId
    }

    const handleModeChange = (mode) => {
      chatMode.value = mode
    }

    const handleSettings = () => {
      // Open chat settings modal
      console.log('Open chat settings')
    }

    const updateTask = async (updates) => {
      if (!updates?.id) return
      // Normalize date-only strings (e.g. "2025-11-12") to RFC3339 midnight UTC
      const normalized = { ...updates }
      const isDateOnly = (s) => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s)
      if (isDateOnly(normalized.due_date)) {
        normalized.due_date = `${normalized.due_date}T00:00:00Z`
      }
      try {
        await store.dispatch('tasks/update', {
          id: normalized.id,
          updates: normalized
        })
        // Update local task list
        const index = tasks.value.findIndex(t => t.id === normalized.id)
        if (index !== -1) {
          tasks.value[index] = { ...tasks.value[index], ...normalized }
        }

        // Background: refresh workstream then goals (in this order)
        ;(async () => {
          try {
            await store.dispatch('workstreams/fetchById', id.value)
            await wsHeader.value?.refreshGoals?.()
          } catch (e) { /* noop */ }
        })()
      } catch (error) {
        console.error('Failed to update task:', error)
      }
    }


    const deleteTask = async (task) => {
      if (!task?.id) return
      if (!confirm('Delete this task?')) return
      try {
        // Directly call API: DELETE /api/tasks/:id (Authorization header provided by axios interceptor)
        await tasksApi.delete(task.id)
        // Update local list
        tasks.value = tasks.value.filter(t => t.id !== task.id)
      } catch (error) {
        console.error('Failed to delete task:', error)
        alert('Failed to delete task')
      }
    }

    const updateDescription = async (description) => {
      try {
        await store.dispatch('workstreams/update', {
          id: id.value,
          updates: { description }
        })
        // Refresh to get updated data
        await store.dispatch('workstreams/fetchById', id.value)
      } catch (error) {
        console.error('Failed to update description:', error)
      }
    }

    const updateGoals = async (goals) => {
      try {
        await store.dispatch('workstreams/update', {
          id: id.value,
          updates: { goals }
        })
        // Refresh to get updated data
        await store.dispatch('workstreams/fetchById', id.value)
      } catch (error) {
        console.error('Failed to update goals:', error)
      }
    }
    

    
    // Drag and Drop handlers
    const draggedTask = ref(null)

    // Ref to WorkstreamHeader to trigger background goal progress refresh
    const wsHeader = ref(null)
    
    const onDragStart = (event, task) => {
      console.log('🟢 Drag started:', task.title)
      draggedTask.value = task
      event.dataTransfer.effectAllowed = 'move'
      // Required for some browsers (Firefox, Safari)
      event.dataTransfer.setData('text/plain', task.id)
    }
    
    const onDrop = async (event, newStatus) => {
      event.preventDefault()
      console.log('🟢 Drop event:', newStatus, 'Task:', draggedTask.value?.title)
      if (!draggedTask.value || draggedTask.value.status === newStatus) {
        console.log('⚠️ Skipping - no task or same status')
        draggedTask.value = null
        return
      }
      
      const taskToUpdate = draggedTask.value
      const oldStatus = taskToUpdate.status || 'todo'
      
      // Optimistically update UI
      taskToUpdate.status = newStatus
      draggedTask.value = null
      
      // Update via API
      try {
        console.log('🔵 Updating task via API:', taskToUpdate.id, 'to status:', newStatus)
        await store.dispatch('tasks/update', {
          id: taskToUpdate.id,
          updates: { status: newStatus }
        })
        console.log('✅ Task updated successfully')
        // If moved to done, complete task and then refresh in background
        if (newStatus === 'done') {
          store.dispatch('tasks/complete', { id: taskToUpdate.id, data: {} })
            .catch(() => {})
            .finally(() => {
              // Background: call Workstream then Goals (in order)
              ;(async () => {
                try {
                  await store.dispatch('workstreams/fetchById', id.value)
                  await wsHeader.value?.refreshGoals?.()
                } catch (e) { /* noop */ }
              })()
            })
        } else {
          // Background: call Workstream then Goals (in order)
          ;(async () => {
            try {
              await store.dispatch('workstreams/fetchById', id.value)
              await wsHeader.value?.refreshGoals?.()
            } catch (e) { /* noop */ }
          })()
        }
      } catch (error) {
        console.error('Failed to update task status:', error)
        // Revert on failure
        taskToUpdate.status = oldStatus
        alert('Failed to update task status')
      }
    }
    
    // Quick add task with pre-filled status
    const quickAddTask = (status) => {
      newTask.value = { title: '', description: '', status, goal_id: '', allocated_tokens: null }
      showCreateTaskForm.value = true
    }

    const createTask = async () => {
      try {
        const payload = { 
          workstream_id: id.value, 
          title: newTask.value.title,
          description: newTask.value.description,
          goal_id: newTask.value.goal_id || null,
          token_allocated: newTask.value.allocated_tokens
        }
        // Normalize due_date to end-of-day (23:59:59Z) when date-only is provided
        const isDateOnly = (s) => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s)
        if (newTask.value.due_date) {
          payload.due_date = isDateOnly(newTask.value.due_date) 
            ? `${newTask.value.due_date}T23:59:59Z` 
            : newTask.value.due_date
        }
        // Include assignees in create payload as array of user IDs
        const assigneeIds = (createSelectedAssignees.value || []).map(x => getId(x)).filter(Boolean)
        if (assigneeIds.length) {
          payload.assignees = assigneeIds
        }
        await store.dispatch('tasks/create', payload)
        showCreateTaskForm.value = false
        newTask.value = { title: '', description: '', goal_id: '', allocated_tokens: null }
        createSelectedAssignees.value = []
        createAssigneeQuery.value = ''
        await refreshData()
        // Refresh goals in header component to update progress immediately
        await wsHeader.value?.refreshGoals?.()
      } catch (error) {
        console.error('Failed to create task:', error)
      }
    }

    const getInitials = (name) => {
      if (!name) return '?'
      return name
        .split(/[\s@]/)
        .filter(part => part)
        .map(part => part[0].toUpperCase())
        .slice(0, 2)
        .join('')
    }

    const formatDate = (date) => {
      if (!date) return 'Unknown'
      const d = new Date(date)
      const now = new Date()
      const diffTime = Math.abs(now - d)
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      
      if (diffDays === 0) return 'Today'
      if (diffDays === 1) return 'Yesterday'
      if (diffDays < 7) return `${diffDays} days ago`
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`
      return d.toLocaleDateString()
    }

onMounted(async () => {
      await refreshData()
      // Auto-load workstream chat sessions
      await loadWorkstreamSessions()
      // Load models for chat
      await loadModels()
      // Load workstream files
      await loadWsFiles()
    })

    const bootstrapAgent = async () => {
      try {
        bootstrapLoading.value = true
        const res = await workstreamsApi.bootstrapAgent(id.value)
        const agentId = res?.agent_id || res?.agent?.id
        if (agentId) workstreamAgentId.value = String(agentId)
        // Refresh ws so metadata persists
        await store.dispatch('workstreams/fetchById', id.value)
      } catch (e) {
        console.error('Failed to bootstrap workstream agent', e)
      } finally {
        bootstrapLoading.value = false
      }
    }

    // Load agents and open selection modal
    const loadAgents = async () => {
      try {
        agentsLoading.value = true
        const data = await agentsApi.getAll()
        const list = Array.isArray(data) ? data : (data?.agents || [])
        agentsList.value = list
        if (!selectedAgentChoice.value && list.length > 0) {
          selectedAgentChoice.value = String(list[0].id || list[0]._id || '')
        }
      } finally {
        agentsLoading.value = false
      }
    }

    const openAgentSelectModal = async () => {
      showAgentSelectModal.value = true
      await loadAgents()
    }

    const confirmAgentSelection = async () => {
      if (!selectedAgentChoice.value) return
      try {
        bootstrapLoading.value = true
        // Use new API: PUT /api/workstreams/:workstream_id/agent with { agent_id }
        await workstreamsApi.assignAgent(id.value, selectedAgentChoice.value)
        workstreamAgentId.value = String(selectedAgentChoice.value)
        showAgentSelectModal.value = false
        await store.dispatch('workstreams/fetchById', id.value)
      } catch (e) {
        console.error('Failed to set workstream agent', e)
        alert('Failed to assign agent. Please try again.')
      } finally {
        bootstrapLoading.value = false
      }
    }

    const goalLabelFor = (t) => {
      if (!t?.goal_id) return null
      const g = goals.value.find(x => x.id === t.goal_id)
      return g ? (g.title || g.description || g.name || g.id) : t.goal_id
    }

    const tokenTotalFor = (t) => {
      const r = (t?.token_allocated !== undefined) ? t.token_allocated : t?.token_reward
      if (r === 0) return 0
      if (!r) return null
      if (typeof r === 'number') return r
      if (typeof r === 'object') {
        if (typeof r.total === 'number') return r.total
        // Sum numeric leaf or string-number values
        return Object.values(r).reduce((sum, v) => {
          const n = typeof v === 'number' ? v : parseFloat(v)
          return sum + (Number.isFinite(n) ? n : 0)
        }, 0)
      }
      return null
    }

    // Determine current user's role in this workstream
    // For now, default to 'lead' for demo - TODO: get from backend
    const currentUserRole = computed(() => {
      // You can enhance this by checking user permissions or workstream membership
      // For now, allow admin users to manage members
      return isAdminOrAbove.value ? 'lead' : 'observer'
    })

    // Workstream files upload state and handlers
    const wsSelectedFile = ref(null)
    const wsUploading = ref(false)
    const wsUploadMessage = ref('')
    const wsUploadError = ref('')

    const onWsFileSelect = (e) => {
      wsUploadMessage.value = ''
      wsUploadError.value = ''
      const f = e?.target?.files?.[0]
      wsSelectedFile.value = f || null
    }

    const wsFiles = ref([])
    const wsFilesLoading = ref(false)
    const wsFilesError = ref('')

    const loadWsFiles = async () => {
      wsFilesLoading.value = true
      wsFilesError.value = ''
      try {
        const token = localStorage.getItem('token')
        const base = window.location.origin
        // Highest priority: knowledge base documents if kbId is available
        const safeParse = async (resp) => {
          // Prefer JSON, but gracefully fall back to text (HTML login pages were causing parse errors)
          const text = await resp.text()
          try {
            return JSON.parse(text)
          } catch (_) {
            const ct = resp.headers.get('content-type') || ''
            // If we got HTML (e.g., auth redirect or 404 page), surface a clearer message instead of throwing JSON parse error
            if (ct.includes('text/html')) {
              throw new Error('Not Working - Contact admin')
            }
            // Otherwise return raw text as a string so upstream callers can still render something
            return text
          }
        }

        if (kbId.value) {
          try {
            const urlKbDocs = new URL(`/api/knowledge-bases/${kbId.value}/documents`, base)
            const respKb = await fetch(urlKbDocs.toString(), { headers: { 'Authorization': `Bearer ${token}` } })
            if (respKb.ok) {
              const data = await safeParse(respKb)
              const list = Array.isArray(data) ? data : (Array.isArray(data.documents) ? data.documents : (Array.isArray(data.items) ? data.items : []))
              wsFiles.value = list
              return
            }
          } catch (_) { /* continue */ }
        }
        // Next: workstream-scoped documents API
        try {
          const urlWsDocs = new URL(`/api/workstreams/${id.value}/documents`, base)
          const respWs = await fetch(urlWsDocs.toString(), { headers: { 'Authorization': `Bearer ${token}` } })
          if (respWs.ok) {
            const data = await safeParse(respWs)
            const list = Array.isArray(data) ? data : (Array.isArray(data.documents) ? data.documents : (Array.isArray(data.items) ? data.items : []))
            wsFiles.value = list
            return
          }
        } catch (_) { /* fall back to memory listing */ }
        // Fallback: global memory files filtered by workstream metadata
        const url = new URL('/api/memory/files', base)
        if (id.value) {
          url.searchParams.append('workstream_id', id.value)
          url.searchParams.append('meta.workstream_id', id.value)
        }
        const resp = await fetch(url.toString(), { headers: { 'Authorization': `Bearer ${token}` } })
        if (!resp.ok) {
          const text = await resp.text()
          throw new Error(text || 'Failed to load files')
        }
        const data = await safeParse(resp)
        const list = Array.isArray(data) ? data : (Array.isArray(data.items) ? data.items : [])
        const filtered = list.filter(f => {
          const meta = f?.metadata || f?.meta || {}
          return String(meta.workstream_id || '') === String(id.value || '')
        })
        wsFiles.value = filtered
      } catch (e) {
        console.error('Failed to load workstream files', e)
        wsFiles.value = []
        wsFilesError.value = e?.message || 'Failed to load files'
      } finally {
        wsFilesLoading.value = false
      }
    }

    const getFileType = (contentType) => {
      if (contentType && contentType.includes('pdf')) return 'PDF'
      if (contentType && (contentType.includes('word') || contentType.includes('document'))) return 'Word'
      if (contentType && contentType.includes('text')) return 'Text'
      return 'Document'
    }

    const formatFileSizeWs = (bytes) => {
      if (!Number.isFinite(bytes)) return ''
      if (bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
    }

    const downloadWsFile = async (file) => {
      try {
        const token = localStorage.getItem('token')
        const base = window.location.origin
        const desiredName = wsFileDisplayName(file)
        // Try KB document download first (if listed from KB)
        let resp = await fetch(`${base}/api/documents/${file.id}/download`, { headers: { 'Authorization': `Bearer ${token}` } })
        if (!resp.ok) {
          // Try workstream documents download
          const wsUrl = `${base}/api/workstreams/${id.value}/documents/${file.id}/download`
          resp = await fetch(wsUrl, { headers: { 'Authorization': `Bearer ${token}` } })
        }
        if (!resp.ok) {
          // Fallback to memory download
          const memUrl = `${base}/api/memory/files/${file.id}/download`
          resp = await fetch(memUrl, { headers: { 'Authorization': `Bearer ${token}` } })
        }
        if (!resp.ok) throw new Error('Download failed')
        const blob = await resp.blob()
        const objUrl = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = objUrl
        a.download = desiredName || `file-${file.id}`
        a.click()
        window.URL.revokeObjectURL(objUrl)
      } catch (e) {
        alert(e?.message || 'Download failed')
      }
    }

    const wsFileDisplayName = (doc) => {
      if (!doc || typeof doc !== 'object') return 'Untitled document'
      const name = (
        doc.name ||
        doc.filename ||
        doc.file_name ||
        doc.original_name ||
        doc.originalName ||
        doc.document_name ||
        doc.doc_name ||
        (doc.file && (doc.file.name || doc.file.filename)) ||
        (doc.metadata && (doc.metadata.filename || doc.metadata.original_name)) ||
        `Document ${doc.id || ''}`.trim()
      )
      return String(name)
    }

    const deleteWsFile = async (file) => {
      if (!confirm(`Delete \"${file.name || file.title || 'file'}\"?`)) return
      try {
        const token = localStorage.getItem('token')
        const base = window.location.origin
        let resp = null
        // Prefer KB-scoped delete when KB_ID is available
        if (kbId.value) {
          const kbUrl = `${base}/api/knowledge-bases/${kbId.value}/documents/${file.id}`
          resp = await fetch(kbUrl, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } })
        }
        if (!resp || !resp.ok) {
          // Try workstream documents delete
          const url = `${base}/api/workstreams/${id.value}/documents/${file.id}`
          resp = await fetch(url, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } })
        }
        if (!resp.ok) {
          // Fallback to global documents delete (if supported)
          const docUrl = `${base}/api/documents/${file.id}`
          resp = await fetch(docUrl, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } })
        }
        if (!resp.ok) {
          // Final fallback: memory store delete
          const memUrl = `${base}/api/memory/files/${file.id}`
          resp = await fetch(memUrl, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } })
        }
        if (!resp || !resp.ok) throw new Error('Delete failed')
        await loadWsFiles()
      } catch (e) {
        alert(e?.message || 'Delete failed')
      }
    }

    const uploadWsFile = async () => {
      if (!wsSelectedFile.value) return
      wsUploading.value = true
      wsUploadMessage.value = ''
      wsUploadError.value = ''
      try {
        // Use memory upload endpoint with workstream metadata, similar to AgentMemoryManager
        const token = localStorage.getItem('token')
        const formData = new FormData()
        formData.append('file', wsSelectedFile.value)
        // Optional: include a basic type hint
        const mime = wsSelectedFile.value.type || ''
        if (mime) {
          if (mime.startsWith('image/')) formData.append('type', 'image')
          else if (mime.includes('pdf') || mime.includes('text') || mime.includes('document')) formData.append('type', 'document')
        }
        // Name hint
        if (ws.value?.name) formData.append('title', ws.value.name)

        const base = window.location.origin
        // Use workstream documents API (multipart/form-data)
        const url = new URL(`/api/workstreams/${id.value}/documents`, base)
        // Optional metadata fields per API sample
        if (wsSelectedFile.value?.name) {
          formData.append('title', wsSelectedFile.value.name)
        }
        // description/category/tags can be added later via UI fields
        const resp = await fetch(url.toString(), {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        })
        if (!resp.ok) {
          let msg = 'Upload failed'
          try { const err = await resp.json(); msg = err.error || err.message || msg } catch {}
          throw new Error(msg)
        }
        wsUploadMessage.value = 'File uploaded successfully.'
        // Reset input
        wsSelectedFile.value = null
        const input = document.getElementById('ws-file-input')
        if (input) input.value = ''
        // Refresh files list
        await loadWsFiles()
      } catch (e) {
        console.error('Workstream file upload failed', e)
        wsUploadError.value = e?.message || 'Upload failed'
      } finally {
        wsUploading.value = false
      }
    }

    const onMembersLoaded = (members) => {
      // Update member count in workstream header if needed
      if (ws.value) {
        ws.value.member_count = members.length
      }
      // Best-effort: decorate tasks with assignee_name using member info
      try {
        const byId = {}
        for (const m of members || []) {
          if (!m?.member_id) continue
          byId[m.member_id] = m.user_name || m.user_email || `User ${m.member_id}`
        }
        if (Array.isArray(tasks.value) && Object.keys(byId).length > 0) {
          tasks.value = tasks.value.map(t => {
            const primaryId = t.assigned_to || (Array.isArray(t.assignees) && t.assignees[0])
            if (primaryId && byId[primaryId]) {
              return { ...t, assignee_name: byId[primaryId] }
            }
            return t
          })
        }
      } catch (e) {
        // Non-fatal: don't break view rendering if decoration fails
        console.warn('Failed to decorate tasks with assignee names', e)
      }
    }

    const onGoalCreated = async (newGoal) => {
      // Add the new goal to our local goals array so it appears in the task creation dropdown
      if (newGoal) {
        goals.value.push(newGoal)
      }
    }

    const onGoalDeleted = async (goalId) => {
      // Remove the deleted goal from our local goals array
      if (goalId) {
        goals.value = goals.value.filter(g => g.id !== goalId)
      }
    }




return {
      ws,
      loading,
      tasks,
      goals,
      selectedGoalFilter,
      isAdminOrAbove,
      tasksByStatus,
      isTaskFormValid,
      workstreamMessages,
      workstreamChatLoading,
      selectedModel,
      chatMode,
      models,
      chatDrawerOpen,
      wsHeader,
      updateTask,
      deleteTask,
      updateDescription,
      onDragStart,
      onDrop,
      quickAddTask,
      showCreateTaskForm,
      newTask,
      createTask,
      refreshData,
      getInitials,
      formatDate,
      goalLabelFor,
      tokenTotalFor,
      selectedGoalDetails,
      // Workstream chat
      workstreamAgentId,
      // Knowledge base association
      kbId,
      bootstrapLoading,
      bootstrapAgent,
      // Agent selection modal
      showAgentSelectModal,
      agentsList,
      agentsLoading,
      selectedAgentChoice,
      openAgentSelectModal,
      confirmAgentSelection,

      id,
      // Members management
      currentUserRole,
      onMembersLoaded,
      onGoalCreated,
      onGoalDeleted,
      // UnifiedChat handlers
      handleQuickPrompt,
      handleModelChange,
      handleModeChange,
      handleSettings,
      // Workstream files upload
      wsSelectedFile,
      wsUploading,
      wsUploadMessage,
      wsUploadError,
      wsFiles,
      wsFilesLoading,
      wsFilesError,
      onWsFileSelect,
      uploadWsFile,
      loadWsFiles,
      getFileType,
      formatFileSizeWs,
      downloadWsFile,
      deleteWsFile,
      wsFileDisplayName,
      // Create Task assignee search helpers
      createAssigneeQuery,
      createAssigneeOpen,
      createAssigneeResults,
      createAssigneeInputRef,
      createHighlightedIndex,
      createSelectedAssignees,
      createSelectedAssigneeChips,
      displayFor,
      onCreateAssigneeInput,
      createMoveAssignee,
      createConfirmHighlighted,
      createSelectAssignee,
      createRemoveAssignee,
      focusCreateAssignee,
      newTaskDueDateInput,
      selectedModel,
      models,
      workstreamChatLoading,
      handleModelChange,
      sendChat,
      openTask
    }
    }
  }
</script>
